import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import * as authService from '../services/authService';

// Тип пользователя
interface User {
  id: string;
  email: string;
  username: string;
}

// Тип для контекста аутентификации
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

// Создаем контекст с начальным значением
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Пропсы для провайдера контекста
interface AuthProviderProps {
  children: ReactNode;
}

// Компонент провайдера контекста аутентификации
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Функция загрузки данных пользователя
  const loadUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error('Ошибка при загрузке пользователя:', err);
      setError('Не удалось загрузить данные пользователя');
    } finally {
      setIsLoading(false);
    }
  };

  // Обработка токена из URL (для OAuth авторизации)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (token) {
      // Сохраняем токен
      authService.saveToken(token);
      
      // Удаляем параметр из URL без перезагрузки страницы
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Загружаем данные пользователя после сохранения токена
      loadUser();
    }
  }, []);

  // Загрузка данных пользователя при инициализации
  useEffect(() => {
    loadUser();
  }, []);

  // Функция для входа
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.login(email, password);
      setUser(response.user);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Произошла ошибка при входе');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Функция для регистрации
  const register = async (email: string, password: string, username: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.register(email, password, username);
      setUser(response.user);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Произошла ошибка при регистрации');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Функция для выхода
  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Функция для очистки ошибок
  const clearError = () => {
    setError(null);
  };

  // Значение контекста
  const value = {
    user,
    isLoading,
    error,
    login,
    register,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Хук для использования контекста аутентификации
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  
  return context;
};

export default AuthContext; 